import { useState } from "react";
import { api, useApi, relative, ApiError } from "../lib/api";
import { NeedsRole, useGrant } from "../lib/grant";

/** The one path this screen lists (GET) and creates at (POST); the role table keys on it. */
export const SERVICE_TARGETS_PATH = "/v1/service-targets";

interface ServiceTarget {
  id: string;
  name: string;
  kind: string;
  createdAt: string;
}

export function ServiceTargets() {
  const { data, loading, error, reload } = useApi<{ serviceTargets: ServiceTarget[] }>(SERVICE_TARGETS_PATH);
  const grant = useGrant(SERVICE_TARGETS_PATH);
  const [name, setName] = useState("");
  const [kind, setKind] = useState("service");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      await api(SERVICE_TARGETS_PATH, { method: "POST", body: JSON.stringify({ name, kind }) });
      setName("");
      reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? `${err.message}${err.hint ? ` — ${err.hint}` : ""}` : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="panel">
        <h2>Register a service target</h2>
        <p className="muted">
          The one placeholder domain table. A creation writes an audit event in the same transaction.
        </p>
        {/* Configuration: ADMIN. A lower grant sees the line, not a form that can only fail on submit. */}
        {grant.allowed ? (
          <form onSubmit={create} data-testid="write-form" data-path={SERVICE_TARGETS_PATH} style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="name" aria-label="name" required
              style={{ flex: 1, padding: 8, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, color: "var(--text)" }} />
            <input value={kind} onChange={(e) => setKind(e.target.value)} placeholder="kind" aria-label="kind"
              style={{ width: 140, padding: 8, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 6, color: "var(--text)" }} />
            <button disabled={busy} type="submit"
              style={{ padding: "8px 16px", background: "var(--accent)", border: 0, borderRadius: 6, color: "#fff", cursor: "pointer" }}>
              {busy ? "…" : "Register"}
            </button>
          </form>
        ) : <NeedsRole path={SERVICE_TARGETS_PATH} />}
        {problem && <p className="err" style={{ marginTop: 8 }}>{problem}</p>}
      </div>

      <div className="panel">
        <h2>Targets</h2>
        {loading && <p className="muted">Loading…</p>}
        {error && <p className="err">{error.message}</p>}
        {data && data.serviceTargets.length === 0 && <p className="muted">None yet.</p>}
        {data && data.serviceTargets.length > 0 && (
          <table>
            <thead><tr><th>Name</th><th>Kind</th><th>Registered</th></tr></thead>
            <tbody>
              {data.serviceTargets.map((t) => (
                <tr key={t.id} data-testid="target-row">
                  <td>{t.name}</td>
                  <td className="muted">{t.kind}</td>
                  <td className="muted">{relative(t.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
